class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    document.addEventListener('cart:refresh', this.onExternalCartRefresh.bind(this));
    document.addEventListener('cart:updated', this.onExternalCartRefresh.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    if (cartLink.dataset.cartDrawerBound === 'true') return;
    cartLink.dataset.cartDrawerBound = 'true';
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    const drawerHTML = parsedState.sections?.['cart-drawer'];
    if (drawerHTML) {
      this.syncEmptyState(drawerHTML);
    } else if (parsedState.item_count !== undefined) {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', parsedState.item_count === 0);
      this.open();
    });
  }

  async refreshFromCart() {
    if (this.refreshing) return;
    this.refreshing = true;

    try {
      const response = await fetch(
        `${routes.cart_url}?sections=cart-drawer,cart-icon-bubble&sections_url=${window.location.pathname}`
      );
      const sections = await response.json();

      const drawerHTML = sections['cart-drawer'];
      const iconHTML = sections['cart-icon-bubble'];

      if (drawerHTML) {
        const drawerDOM = this.getSectionDOM(drawerHTML, 'cart-drawer');
        const sourceInner = drawerDOM?.querySelector('.drawer__inner');
        const targetInner = this.querySelector('.drawer__inner');

        if (sourceInner && targetInner) {
          targetInner.replaceWith(sourceInner);
          this.classList.toggle('is-empty', drawerDOM.classList.contains('is-empty'));
        }
      }

      if (iconHTML) {
        const iconTarget = document.getElementById('cart-icon-bubble');
        const iconSource = this.getSectionDOM(iconHTML);
        if (iconTarget && iconSource) iconTarget.innerHTML = iconSource.innerHTML;
      }

      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.setHeaderCartIconAccessibility();
      if (this.pendingMessage) this.showMessage(this.pendingMessage);
      this.open();
    } catch (error) {
      console.error(error);
    } finally {
      this.refreshing = false;
    }
  }

  async onExternalCartRefresh(event) {
    const message = event.detail?.message;
    await this.refreshFromCart();
    if (message) this.showMessage(message);
  }

  showMessage(message) {
    this.pendingMessage = message;
    const messageElement = this.getMessageElement();
    if (!messageElement) return;

    window.clearTimeout(this.messageTimeout);
    messageElement.textContent = message;
    messageElement.hidden = false;

    this.messageTimeout = window.setTimeout(() => {
      messageElement.hidden = true;
      messageElement.textContent = '';
      this.pendingMessage = '';
    }, 3500);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  syncEmptyState(html) {
    const drawerDOM = this.getSectionDOM(html, 'cart-drawer');
    if (!drawerDOM) return;

    this.classList.toggle('is-empty', drawerDOM.classList.contains('is-empty'));
  }

  getMessageElement() {
    let messageElement = this.querySelector('[data-cart-drawer-message]');
    const footer = this.querySelector('.drawer__footer');
    if (!footer) return null;

    if (!messageElement) {
      messageElement = document.createElement('div');
      messageElement.className = 'cart-drawer__message';
      messageElement.setAttribute('data-cart-drawer-message', '');
      messageElement.setAttribute('role', 'alert');
      messageElement.hidden = true;
      footer.prepend(messageElement);
    }

    return messageElement;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
